import { useState, useEffect, useRef, useCallback } from "react";

const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60s heartbeat to keep claim alive
const CLAIM_TTL_MS = 5 * 60 * 1000;      // must match server TTL

const API_BASE = window.location.href.includes('localhost') ? "http://localhost:4200/api" : "https://api-curate.pyther.com/api";

export default function ShipmentValidator() {

  const user = JSON.parse(localStorage.getItem('user'));
  const CURRENT_USER = {
    userId: user.userId,
    name: user.name,
    email: user.email,
  };

  const [records, setRecords] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCas, setNewCas] = useState("");
  const [newUpn, setNewUpn] = useState("");
  const [customUpns, setCustomUpns] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [note, setNote] = useState("");

  // ── ADDON: lock state ──
  const [lockStatus, setLockStatus] = useState(null); // null | { locked: true, claimedBy, claimExpiresAt }
  const heartbeatRef = useRef(null);
  const currentRecordIdRef = useRef(null);

  useEffect(() => {
    fetchQueue();
    // ── ADDON: release claim on tab close / refresh ──
    const handleUnload = () => {
      if (currentRecordIdRef.current) {
        releaseClaim(currentRecordIdRef.current);
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // ── ADDON: claim/release on record navigation ──
  useEffect(() => {
    if (!records.length) return;
    const rec = records[current];
    if (!rec) return;
    claimRecord(rec._id);
    return () => {
      // release when navigating away
      if (currentRecordIdRef.current) {
        releaseClaim(currentRecordIdRef.current);
      }
    };
  }, [current, records.length]);

  // ── ADDON: heartbeat keeps claim alive every 60s ──
  const startHeartbeat = useCallback((recordId) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      try {
        await fetch(`${API_BASE}/shipments/${recordId}/heartbeat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: CURRENT_USER.userId }),
        });
      } catch (_) {}
    }, HEARTBEAT_INTERVAL_MS);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // ── ADDON: claim a record ──
  const claimRecord = async (recordId) => {
    stopHeartbeat();
    currentRecordIdRef.current = recordId;
    try {
      const res = await fetch(`${API_BASE}/shipments/${recordId}/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(CURRENT_USER),
      });
      if (res.status === 409) {
        const data = await res.json();
        setLockStatus({ locked: true, ...data });
        return;
      }
      setLockStatus(null);
      startHeartbeat(recordId);
    } catch (_) {
      setLockStatus(null); // fail open — don't block on network error
    }
  };

  // ── ADDON: release a claim ──
  const releaseClaim = async (recordId) => {
    stopHeartbeat();
    try {
      await fetch(`${API_BASE}/shipments/${recordId}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: CURRENT_USER.userId }),
      });
    } catch (_) {}
    currentRecordIdRef.current = null;
  };

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/shipments/validation-queue`);
      const data = await res.json();
      setRecords(data.records);
    } catch (e) {
      showToast("Failed to load queue", "error");
    } finally {
      setLoading(false);
    }
  };

  const rec = records[current];
  const allCasUpns = rec
    ? [...(rec.casUpns || []), ...(customUpns[rec._id] || [])]
    : [];

  const handleSelect = (cu) => {
    setSelected(cu);
  };

  const handleValidate = async () => {
    if (!selected || !rec) return;
    const [cas, upn] = selected.split("||");
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/shipments/${rec._id}/validate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          finalCas: cas,
          finalUpn: upn,
          validatedBy: CURRENT_USER,
          note: note.trim() || null,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      const updated = await res.json();
      setRecords((prev) =>
        prev.map((r) => (r._id === updated._id ? updated : r))
      );
      setNote("");
      showToast(`Validated: ${upn}`);
      // ── ADDON: release claim after successful validation ──
      await releaseClaim(rec._id);
      setLockStatus(null);
    } catch (e) {
      showToast("Save failed. Please retry.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCustom = async () => {
    if (!newCas.trim() || !newUpn.trim()) return;
    const cu = `${newCas.trim().toUpperCase()}||${newUpn.trim().toUpperCase()}`;
    try {
      await fetch(`${API_BASE}/shipments/${rec._id}/add-upn`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cas: newCas.trim(), upn: newUpn.trim() }),
      });
    } catch (_) {}
    setCustomUpns((prev) => ({
      ...prev,
      [rec._id]: [...(prev[rec._id] || []), cu],
    }));
    setSelected(cu);
    setNewCas("");
    setNewUpn("");
    setShowAddForm(false);
  };

  const navigate = (dir) => {
    setCurrent((c) => Math.max(0, Math.min(records.length - 1, c + dir)));
    setSelected(null);
    setShowAddForm(false);
    setShowHistory(false);
    setNote("");
    setNewCas("");
    setNewUpn("");
    // ── ADDON: reset lock status on navigate ──
    setLockStatus(null);
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const validated = records.filter(
    (r) => r.validationStatus === "validated"
  ).length;
  const pending = records.length - validated;

  if (loading) return <div style={s.loadingWrap}>Loading queue...</div>;
  if (!records.length)
    return <div style={s.loadingWrap}>No records to validate.</div>;
  if (!rec) return null;

  const casCount = (rec.uniqueCasList || []).length;
  const upnCount = (rec.uniqueUpnList || []).length;
  const history = rec.validationHistory || [];
  const latest = rec.latestValidation;

  // ── ADDON: parse casNos map for CAS count display ──
  const casNosMap = rec.casNos
    ? typeof rec.casNos.entries === "function"
      ? Object.fromEntries(rec.casNos.entries())
      : rec.casNos
    : {};

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.headerRow}>
        <h1 style={s.pageTitle}>UPN Validation Queue</h1>
        <span style={s.pill}>
          Record {current + 1} of {records.length}
        </span>
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        {[
          { label: "Total", val: records.length, color: "#111" },
          { label: "Validated", val: validated, color: "#1D9E75" },
          { label: "Pending", val: pending, color: "#BA7517" },
        ].map((st) => (
          <div key={st.label} style={s.statCard}>
            <div style={{ ...s.statNum, color: st.color }}>{st.val}</div>
            <div style={s.statLbl}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div style={s.navRow}>
        <button
          style={s.navBtn}
          onClick={() => navigate(-1)}
          disabled={current === 0}
        >
          ← Prev
        </button>
        <span style={s.navPos}>
          {current + 1} / {records.length}
        </span>
        <button
          style={s.navBtn}
          onClick={() => navigate(1)}
          disabled={current === records.length - 1}
        >
          Next →
        </button>
      </div>

      {/* ── ADDON: Lock banner — shown when record is claimed by someone else ── */}
      {lockStatus?.locked && (
        <div style={s.lockBanner}>
          <div style={s.lockIcon}>🔒</div>
          <div>
            <div style={s.lockTitle}>
              Record locked by {lockStatus.claimedBy?.name}
            </div>
            <div style={s.lockMeta}>
              {lockStatus.claimedBy?.email} · Expires{" "}
              {lockStatus.claimExpiresAt
                ? new Date(lockStatus.claimExpiresAt).toLocaleTimeString()
                : ""}
            </div>
          </div>
          <button
            style={s.lockSkipBtn}
            onClick={() => navigate(1)}
          >
            Skip to next →
          </button>
        </div>
      )}

      {/* Record Card */}
      <div style={{
        ...s.card,
        // ── ADDON: visually dim card when locked ──
        ...(lockStatus?.locked ? s.cardLocked : {}),
      }}>
        {/* Key row */}
        <div style={s.cardLabel}>Record</div>
        <div style={s.keyRow}>
          <span style={s.keyBadge}>{rec.key}</span>
          {rec.validationStatus === "validated" && (
            <span style={s.validatedBadge}>Validated</span>
          )}
          {/* ── ADDON: lock badge ── */}
          {lockStatus?.locked && (
            <span style={s.lockBadge}>
              🔒 Locked by {lockStatus.claimedBy?.name}
            </span>
          )}
          {history.length > 0 && (
            <button
              style={s.historyToggle}
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory ? "Hide" : "Show"} history ({history.length})
            </button>
          )}
        </div>
        <div style={s.metaRow}>
          <span>{rec.DATE?.substring(0, 10)}</span>
          <span>{rec.SOURCEOFCOUNTRY}</span>
          <span>
            Curated:{" "}
            <strong style={{ fontWeight: 500 }}>{rec.curatedProduct}</strong>
          </span>
        </div>

        <div style={s.divider} />

        {/* Info grid */}
        <div style={s.sectionTitle}>Record details</div>
        <div style={s.infoGrid}>
          <InfoCell label="Product count" value={rec.productCount} color="blue" />
          <InfoCell label="CAS count" value={casCount} color="blue" />
          <InfoCell label="UPN count" value={upnCount} color="blue" />
          <InfoCell label="Latest CAS" value={rec.latestCas} mono />
          <InfoCell label="Latest UPN" value={rec.latestUpn} mono />
          <InfoCell
            label="Validation status"
            value={rec.validationStatus || "pending"}
            color={rec.validationStatus === "validated" ? "green" : "muted"}
          />
          <InfoCell
            label="Majority CAS"
            value={rec?.majorityCas}
            color={rec?.finalCas ? "green" : null}
            mono
          />
          <InfoCell
            label="Majority UPN"
            value={rec?.majorityUpn}
            color={rec?.finalUpn ? "green" : null}
            mono
            span={2}
          />
          <InfoCell
            label="Final CAS"
            value={latest?.finalCas}
            color={latest?.finalCas ? "green" : null}
            mono
          />
          <InfoCell
            label="Final UPN"
            value={latest?.finalUpn}
            color={latest?.finalUpn ? "green" : null}
            mono
            span={2}
          />
        </div>

        {/* ── ADDON: CAS breakdown from casNos map ── */}
        {Object.keys(casNosMap).length > 0 && (
          <>
            <div style={s.divider} />
            <div style={s.sectionTitle}>
              CAS shipment record counts
            </div>
            <div style={s.casCountGrid}>
              {Object.entries(casNosMap)
                .sort((a, b) => b[1] - a[1])
                .map(([cas, count]) => {
                  const matchedUpn = (rec.casUpns || [])
                    .find((cu) => cu.startsWith(cas + "||"))
                    ?.split("||")[1] || null;
                  const total = Object.values(casNosMap).reduce(
                    (a, v) => a + v, 0
                  );
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  const isSelectedCas =
                    selected && selected.startsWith(cas + "||");
                  return (
                    <div
                      key={cas}
                      style={{
                        ...s.casCountItem,
                        ...(isSelectedCas ? s.casCountItemSel : {}),
                      }}
                      onClick={() => {
                        if (lockStatus?.locked) return;
                        const cu = rec.casUpns?.find((c) =>
                          c.startsWith(cas + "||")
                        );
                        if (cu) handleSelect(cu);
                      }}
                    >
                      <div style={s.casCountTop}>
                        <span style={{
                          ...s.casCountCode,
                          ...(isSelectedCas ? { color: "#0C447C" } : {}),
                        }}>
                          {cas}
                        </span>
                        <span style={s.casCountNum}>{count} records</span>
                        <span style={{
                          ...s.casCountPct,
                          color: pct >= 70 ? "#1D9E75" : pct >= 40 ? "#BA7517" : "#aaa",
                        }}>
                          {pct}%
                        </span>
                      </div>
                      {matchedUpn && (
                        <div style={s.casCountUpn}>{matchedUpn}</div>
                      )}
                      {/* progress bar */}
                      <div style={s.casCountBarBg}>
                        <div
                          style={{
                            ...s.casCountBarFill,
                            width: `${pct}%`,
                            background: isSelectedCas
                              ? "#378ADD"
                              : pct >= 70
                              ? "#1D9E75"
                              : pct >= 40
                              ? "#BA7517"
                              : "#ccc",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* Latest validated by */}
        {latest && (
          <>
            <div style={s.divider} />
            <div style={s.sectionTitle}>Latest validated by</div>
            <div style={s.validatedByBox}>
              <div style={s.avatarCircle}>
                {latest.validatedBy?.name
                  ?.split(" ")
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
              <div>
                <div style={s.vbName}>{latest.validatedBy?.name}</div>
                <div style={s.vbEmail}>{latest.validatedBy?.email}</div>
                <div style={s.vbMeta}>
                  ID: {latest.validatedBy?.userId} &nbsp;·&nbsp;
                  {latest.createdAt
                    ? new Date(latest.createdAt).toLocaleString()
                    : ""}
                </div>
                {latest.note && (
                  <div style={s.vbNote}>Note: {latest.note}</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Validation history */}
        {showHistory && history.length > 0 && (
          <>
            <div style={s.divider} />
            <div style={s.sectionTitle}>
              Validation history ({history.length})
            </div>
            <div style={s.historyList}>
              {[...history].reverse().map((h, i) => (
                <div key={i} style={s.historyItem}>
                  <div style={s.historyHeader}>
                    <div style={s.avatarCircleSmall}>
                      {h.validatedBy?.name
                        ?.split(" ")
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div>
                      <div style={s.vbName}>{h.validatedBy?.name}</div>
                      <div style={s.vbEmail}>{h.validatedBy?.email}</div>
                    </div>
                    <div style={{ marginLeft: "auto", textAlign: "right" }}>
                      <div style={s.historyDate}>
                        {h.createdAt
                          ? new Date(h.createdAt).toLocaleString()
                          : ""}
                      </div>
                    </div>
                  </div>
                  <div style={s.historyFields}>
                    <div style={s.historyField}>
                      <span style={s.historyFieldLabel}>Final CAS</span>
                      <span style={{ ...s.historyFieldValue, color: "#0C447C" }}>
                        {h.finalCas}
                      </span>
                    </div>
                    <div style={s.historyField}>
                      <span style={s.historyFieldLabel}>Final UPN</span>
                      <span style={{ ...s.historyFieldValue, color: "#0C447C" }}>
                        {h.finalUpn}
                      </span>
                    </div>
                  </div>
                  {h.note && (
                    <div style={s.vbNote}>Note: {h.note}</div>
                  )}
                  {h.snapshotRecord && (
                    <details style={s.snapshotWrap}>
                      <summary style={s.snapshotToggle}>
                        View snapshot at time of validation
                      </summary>
                      <div style={s.snapshotGrid}>
                        {Object.entries(h.snapshotRecord).map(([k, v]) => (
                          <div key={k} style={s.snapshotRow}>
                            <span style={s.snapshotKey}>{k}</span>
                            <span style={s.snapshotVal}>
                              {Array.isArray(v)
                                ? v.join(", ")
                                : v === null
                                ? "—"
                                : String(v)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div style={s.divider} />

        {/* Product names */}
        <div style={s.sectionTitle}>Product names</div>
        <div style={s.productList}>
          {rec.productName?.map((n) => (
            <div key={n} style={s.productTag}>
              {n}
            </div>
          ))}
        </div>

        <div style={s.divider} />

        {/* CAS/UPN selector */}
        <div style={s.sectionTitle}>
          Select the correct UPN & CAS for this key
        </div>
        <div style={s.casGrid}>
          {allCasUpns.map((cu) => {
            const [cas, upn] = cu.split("||");
            const isSel = selected === cu;
            return (
              <div
                key={cu}
                style={{
                  ...s.casItem,
                  ...(isSel ? s.casItemSel : {}),
                }}
                onClick={() => handleSelect(cu)}
              >
                <div
                  style={{
                    ...s.casCode,
                    ...(isSel ? { color: "#0C447C" } : {}),
                  }}
                >
                  {cas}
                </div>
                <div
                  style={{
                    ...s.upnName,
                    ...(isSel ? { color: "#185FA5" } : {}),
                  }}
                >
                  {upn}
                </div>
                <div
                  style={{
                    ...s.checkIcon,
                    ...(isSel ? s.checkIconSel : {}),
                  }}
                >
                  {isSel && <div style={s.checkDot} />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add other UPN */}
        <div style={{ marginTop: 12 }}>
          <button
            style={s.addToggle}
            onClick={() => setShowAddForm((v) => !v)}
          >
            + Add other UPN
          </button>
          {showAddForm && (
            <div style={s.addForm}>
              <div style={s.inputRow}>
                <input
                  style={s.input}
                  placeholder="CAS No (e.g. AB9498)"
                  value={newCas}
                  onChange={(e) => setNewCas(e.target.value)}
                />
                <input
                  style={s.input}
                  placeholder="UPN (e.g. SUNITINIB (25 MG))"
                  value={newUpn}
                  onChange={(e) => setNewUpn(e.target.value)}
                />
              </div>
              <button style={s.addBtn} onClick={handleAddCustom}>
                Add & select
              </button>
            </div>
          )}
        </div>

        {/* Note */}
        <div style={{ marginTop: 14 }}>
          <div style={s.sectionTitle}>Note (optional)</div>
          <textarea
            style={s.textarea}
            placeholder="Add a note about this validation decision..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </div>

        {/* Validated by (current user preview) */}
        <div style={s.currentUserRow}>
          <div style={s.avatarCircleSmall}>
            {CURRENT_USER.name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>
              {CURRENT_USER.name}
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {CURRENT_USER.email} · {CURRENT_USER.userId}
            </div>
          </div>
          <span style={s.validatingAsLabel}>Validating as</span>
        </div>

        <div style={s.sectionTitle}>Record details</div>
        <div style={s.infoGrid}>
          <InfoCell
            label="Final CAS || UPN"
            value={selected}
            color={selected ? "green" : null}
            span={4}
          />
        </div>

        {/* Actions */}
        <div style={s.actionRow}>
          <button
            style={{
              ...s.validateBtn,
              // ── ADDON: also disable when locked ──
              ...(!selected || saving || lockStatus?.locked
                ? s.validateBtnDisabled
                : {}),
            }}
            onClick={() => {
              handleValidate()
              navigate(1)
            }}
            disabled={!selected || saving || lockStatus?.locked}
          >
            {saving
              ? "Saving..."
              : lockStatus?.locked
              ? "Locked — cannot validate"
              : !selected
              ? "Select a UPN to confirm"
              : "Confirm validation & Next"}
          </button>
          <button
            style={s.skipBtn}
            onClick={() => navigate(1)}
            disabled={current === records.length - 1}
          >
            Skip →
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...s.toast,
            background: toast.type === "error" ? "#A32D2D" : "#3B6D11",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── InfoCell sub-component — unchanged ──
function InfoCell({ label, value, color, mono, span }) {
  const colorMap = {
    blue: "#0C447C",
    green: "#1D9E75",
    muted: "#aaa",
  };
  const isNull = value === null || value === undefined || value === "";
  return (
    <div
      style={{
        ...s.infoCell,
        ...(span ? { gridColumn: `span ${span}` } : {}),
      }}
    >
      <div style={s.infoCellLabel}>{label}</div>
      <div
        style={{
          ...s.infoCellValue,
          ...(mono ? { fontFamily: "monospace" } : {}),
          color: isNull ? "#aaa" : color ? colorMap[color] : "#111",
          fontStyle: isNull ? "italic" : "normal",
          fontWeight: isNull ? 400 : 500,
        }}
      >
        {isNull ? "— not set" : String(value)}
      </div>
    </div>
  );
}

// ── Styles — all original preserved, addons at bottom ──
const s = {
  page: {
    maxWidth: 'auto',
    margin: "0 auto",
    padding: "2rem 1.25rem",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  loadingWrap: {
    padding: "3rem",
    textAlign: "center",
    color: "#888",
    fontFamily: "system-ui, sans-serif",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "5rem",
    marginBottom: "1.25rem",
  },
  pageTitle: { fontSize: 20, fontWeight: 500, color: "#111" },
  pill: {
    fontSize: 12,
    background: "#f3f3f3",
    border: "0.5px solid #ddd",
    borderRadius: 999,
    padding: "4px 12px",
    color: "#666",
  },
  statsRow: { display: "flex", gap: 8, marginBottom: "1rem" },
  statCard: {
    flex: 1,
    background: "#f7f7f7",
    borderRadius: 8,
    padding: "10px 14px",
  },
  statNum: { fontSize: 22, fontWeight: 500 },
  statLbl: { fontSize: 11, color: "#888", marginTop: 2 },
  navRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: "1rem",
  },
  navBtn: {
    background: "none",
    border: "0.5px solid #ccc",
    borderRadius: 6,
    padding: "5px 12px",
    fontSize: 13,
    color: "#555",
    cursor: "pointer",
  },
  navPos: { flex: 1, textAlign: "center", fontSize: 13, color: "#888" },
  card: {
    background: "#fff",
    border: "0.5px solid #ddd",
    borderRadius: 12,
    padding: "1.75rem",
  },
  cardLabel: {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#aaa",
    marginBottom: 8,
  },
  keyRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  keyBadge: {
    fontSize: 14,
    fontWeight: 500,
    background: "#E6F1FB",
    color: "#0C447C",
    borderRadius: 6,
    padding: "4px 12px",
    fontFamily: "monospace",
  },
  validatedBadge: {
    fontSize: 11,
    background: "#EAF3DE",
    color: "#3B6D11",
    borderRadius: 999,
    padding: "3px 10px",
    fontWeight: 500,
  },
  historyToggle: {
    fontSize: 12,
    color: "#378ADD",
    background: "none",
    border: "0.5px solid #B5D4F4",
    borderRadius: 999,
    padding: "3px 10px",
    cursor: "pointer",
  },
  metaRow: {
    display: "flex",
    gap: 16,
    fontSize: 12,
    color: "#888",
    flexWrap: "wrap",
  },
  divider: { height: 0.5, background: "#eee", margin: "1.1rem 0" },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "#666",
    marginBottom: 10,
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 8,
  },
  infoCell: { background: "#f7f7f7", borderRadius: 8, padding: "10px 12px" },
  infoCellLabel: {
    fontSize: 11,
    color: "#aaa",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  infoCellValue: { fontSize: 13, wordBreak: "break-all" },
  validatedByBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    background: "#f7f7f7",
    borderRadius: 8,
    padding: "12px 14px",
  },
  avatarCircle: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "#E6F1FB",
    color: "#0C447C",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 500,
    flexShrink: 0,
  },
  avatarCircleSmall: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#E6F1FB",
    color: "#0C447C",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 500,
    flexShrink: 0,
  },
  vbName: { fontSize: 13, fontWeight: 500, color: "#111", marginBottom: 2 },
  vbEmail: { fontSize: 12, color: "#888", marginBottom: 2 },
  vbMeta: { fontSize: 11, color: "#aaa" },
  vbNote: {
    fontSize: 12,
    color: "#666",
    marginTop: 6,
    fontStyle: "italic",
    background: "#fff8e1",
    borderRadius: 4,
    padding: "4px 8px",
    border: "0.5px solid #f0d080",
  },
  historyList: { display: "flex", flexDirection: "column", gap: 12 },
  historyItem: {
    background: "#f7f7f7",
    borderRadius: 8,
    padding: "12px 14px",
    border: "0.5px solid #eee",
  },
  historyHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  historyDate: { fontSize: 11, color: "#aaa" },
  historyFields: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 4 },
  historyField: { display: "flex", flexDirection: "column", gap: 2 },
  historyFieldLabel: {
    fontSize: 11,
    color: "#aaa",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  historyFieldValue: {
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "monospace",
  },
  snapshotWrap: { marginTop: 10 },
  snapshotToggle: {
    fontSize: 12,
    color: "#378ADD",
    cursor: "pointer",
    listStyle: "none",
  },
  snapshotGrid: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    background: "#fff",
    borderRadius: 6,
    padding: "10px 12px",
    border: "0.5px solid #eee",
  },
  snapshotRow: {
    display: "flex",
    gap: 12,
    fontSize: 12,
    borderBottom: "0.5px solid #f3f3f3",
    paddingBottom: 4,
  },
  snapshotKey: {
    color: "#aaa",
    minWidth: 140,
    fontFamily: "monospace",
    flexShrink: 0,
  },
  snapshotVal: {
    color: "#444",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  productList: { display: "flex", flexDirection: "column", gap: 6 },
  productTag: {
    fontSize: 13,
    background: "#f7f7f7",
    border: "0.5px solid #ddd",
    borderRadius: 6,
    padding: "6px 12px",
    fontFamily: "monospace",
  },
  casGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  casItem: {
    background: "#f7f7f7",
    border: "0.5px solid #ddd",
    borderRadius: 8,
    padding: "12px 14px",
    position: "relative",
    minHeight: 72,
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  casItemSel: { borderColor: "#378ADD", background: "#E6F1FB" },
  casCode: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "monospace",
    color: "#888",
    marginBottom: 4,
  },
  upnName: { fontSize: 14, fontWeight: 500, color: "#111", paddingRight: 24 },
  checkIcon: {
    position: "absolute",
    top: 10,
    right: 12,
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "1.5px solid #ccc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  checkIconSel: { background: "#378ADD", border: "1.5px solid #378ADD" },
  checkDot: { width: 8, height: 8, background: "white", borderRadius: "50%" },
  addToggle: {
    fontSize: 13,
    color: "#378ADD",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: "4px 0",
  },
  addForm: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 10,
  },
  inputRow: { display: "flex", gap: 8 },
  input: {
    flex: 1,
    fontSize: 13,
    padding: "8px 10px",
    border: "0.5px solid #ccc",
    borderRadius: 6,
    fontFamily: "monospace",
    outline: "none",
  },
  addBtn: {
    fontSize: 13,
    padding: "8px 14px",
    background: "#E6F1FB",
    color: "#0C447C",
    border: "0.5px solid #B5D4F4",
    borderRadius: 6,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  textarea: {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "0.5px solid #ccc",
    borderRadius: 6,
    fontFamily: "system-ui, sans-serif",
    resize: "vertical",
    outline: "none",
    color: "#111",
    background: "#fff",
    boxSizing: "border-box",
  },
  currentUserRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 12,
    padding: "10px 14px",
    background: "#f7f7f7",
    borderRadius: 8,
    border: "0.5px solid #eee",
  },
  validatingAsLabel: {
    marginLeft: "auto",
    fontSize: 11,
    color: "#aaa",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  actionRow: { display: "flex", gap: 10, marginTop: "1.25rem" },
  validateBtn: {
    flex: 1,
    padding: "11px 0",
    background: "#378ADD",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  validateBtnDisabled: {
    background: "#eee",
    color: "#aaa",
    cursor: "not-allowed",
  },
  skipBtn: {
    padding: "11px 20px",
    background: "none",
    border: "0.5px solid #ccc",
    borderRadius: 8,
    fontSize: 14,
    color: "#666",
    cursor: "pointer",
  },
  toast: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    color: "white",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 13,
    zIndex: 99,
  },

  // ── ADDON styles ──
  lockBanner: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#FAECE7",
    border: "0.5px solid #F5C4B3",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: "1rem",
  },
  lockIcon: { fontSize: 18, flexShrink: 0 },
  lockTitle: { fontSize: 13, fontWeight: 500, color: "#712B13" },
  lockMeta: { fontSize: 12, color: "#993C1D", marginTop: 2 },
  lockSkipBtn: {
    marginLeft: "auto",
    fontSize: 13,
    padding: "6px 14px",
    background: "none",
    border: "0.5px solid #F5C4B3",
    borderRadius: 6,
    color: "#712B13",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  cardLocked: {
    opacity: 0.6,
    pointerEvents: "none",
    userSelect: "none",
  },
  lockBadge: {
    fontSize: 11,
    background: "#FAECE7",
    color: "#712B13",
    borderRadius: 999,
    padding: "3px 10px",
    fontWeight: 500,
    border: "0.5px solid #F5C4B3",
  },
  casCountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 8,
  },
  casCountItem: {
    background: "#f7f7f7",
    border: "0.5px solid #ddd",
    borderRadius: 8,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  casCountItemSel: {
    borderColor: "#378ADD",
    background: "#E6F1FB",
  },
  casCountTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  casCountCode: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "monospace",
    color: "#555",
    flex: 1,
  },
  casCountNum: {
    fontSize: 12,
    color: "#888",
    whiteSpace: "nowrap",
  },
  casCountPct: {
    fontSize: 12,
    fontWeight: 500,
    minWidth: 34,
    textAlign: "right",
  },
  casCountUpn: {
    fontSize: 12,
    color: "#555",
    marginBottom: 6,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  casCountBarBg: {
    height: 4,
    background: "#e8e8e8",
    borderRadius: 99,
    overflow: "hidden",
  },
  casCountBarFill: {
    height: "100%",
    borderRadius: 99,
    transition: "width 0.3s",
  },
};